/**
 * WMS Outbound — Sales Order shared types.
 *
 * A SalesOrder is the native WMS demand document for outbound. It can be created
 * standalone (WMS solo) or synced from TMS (link optional). One order is
 * fulfilled by one or more shipments.
 */

/** Where the order originated — WMS runs solo; TMS is an optional integration. */
export enum SalesOrderSource {
  WMS = 'WMS',
  TMS = 'TMS',
}

/**
 * Order-level lifecycle.
 * DRAFT → ALLOCATED (stock reserved) → PICKING → PACKED → SHIPPED. CANCELLED at any point.
 * PARTIALLY_ALLOCATED when only some lines/qty could be reserved.
 */
export enum SalesOrderStatus {
  DRAFT = 'DRAFT',
  ALLOCATED = 'ALLOCATED',
  PARTIALLY_ALLOCATED = 'PARTIALLY_ALLOCATED',
  PICKING = 'PICKING',
  PACKED = 'PACKED',
  SHIPPED = 'SHIPPED',
  CANCELLED = 'CANCELLED',
}

/**
 * Set-level fulfilment constraint for an order's allocation (Phase E2b).
 * Decides what happens when the available stock can't fully cover every line:
 * the order's lines are allocated in ONE transaction and the set condition is
 * checked, then the whole set commits or rolls back.
 */
export enum FulfillmentPolicy {
  /** Default: keep whatever could be allocated; the remainder is backordered. */
  PARTIAL_BACKORDER = 'PARTIAL_BACKORDER',
  /** All-or-nothing: if any line can't be fully allocated, roll back AND cancel (kill) the order. */
  FILL_OR_KILL = 'FILL_OR_KILL',
  /** All-or-nothing: roll back if incomplete, but leave the order open to retry when stock arrives. */
  SHIP_COMPLETE = 'SHIP_COMPLETE',
}

/** Per-line lifecycle, rolled up into the order status. */
export enum SalesOrderLineStatus {
  PENDING = 'PENDING',
  ALLOCATED = 'ALLOCATED',
  PARTIAL = 'PARTIAL',
  PICKED = 'PICKED',
  SHIPPED = 'SHIPPED',
  CANCELLED = 'CANCELLED',
}

/**
 * An allocation reserves a chunk of a specific inventory item to an order line.
 * OPEN = reserved & awaiting pick; PICKED = consumed by a completed pick;
 * CANCELLED = released (reservation returned to available).
 */
export enum AllocationStatus {
  OPEN = 'OPEN',
  PICKED = 'PICKED',
  CANCELLED = 'CANCELLED',
}

/**
 * Soft (logical / ATP) reservation mode. A soft hold reserves QUANTITY only —
 * no location, lot, or task — dropping available-to-promise now and deciding the
 * physical lot/LPN later at hard-promotion time.
 */
export enum SoftReservationMode {
  /** Short-lived hold for an in-progress cart/session — carries an `expires_at`. */
  CART = 'CART',
  /** Order-level hold until its ship date — carries a `ship_date`. */
  ORDER = 'ORDER',
}

/**
 * Soft-reservation lifecycle. SOFT consumes ATP; the other three have released it.
 * `ATP = on_hand − hard_reserved − Σ(SOFT)` — soft & hard draw from one pool.
 */
export enum SoftReservationStatus {
  /** Active logical hold — counts against ATP. */
  SOFT = 'SOFT',
  /** Converted into a physical Allocation (hard reservation). */
  PROMOTED = 'PROMOTED',
  /** Manually/programmatically released — ATP returned. */
  RELEASED = 'RELEASED',
  /** Cart hold passed its expiry — ATP returned. */
  EXPIRED = 'EXPIRED',
}
